import { memo, ReactElement } from 'react'

import isEqual from 'lodash/isEqual'

import { updateTarget } from 'api/Cerebro/orgScoped/amazonAdsApi'
import { useResource } from 'components/EditableFields/localHooks'
import { ARCHIVED } from 'const/resourceStates'
import { userHasManageAmsPermissions } from 'helpers/featurePermissions'
import { usePermissionMessage, useUserHasPermissions } from 'hooks'
import { NonKeywordTargetFact } from 'types'

import { DeleteField } from '../../SharedFields/DeleteField'

type TargetRecord = NonKeywordTargetFact

interface Props<RecordType> {
    dataIndex: string[]
    readOnly: boolean
    record: RecordType
    reloadData: () => void
}

function ArchiveTargetField<RecordType extends TargetRecord>({
    dataIndex = ['target'],
    readOnly,
    record,
    reloadData,
}: Props<RecordType>): ReactElement {
    const hasPermission = useUserHasPermissions(userHasManageAmsPermissions)
    const permissionMessage = usePermissionMessage()
    const { getFromResource, resourceId } = useResource<RecordType>(
        record,
        dataIndex,
        'target'
    )

    const userCanManageTarget = getFromResource(['user_can_manage'], true)

    const getTooltipTitle = (): string | undefined => {
        if (userCanManageTarget) {
            return undefined
        }

        return hasPermission
            ? (getFromResource(['user_can_manage_detail']) ?? permissionMessage)
            : permissionMessage
    }

    return (
        <DeleteField
            disabled={!userCanManageTarget || readOnly}
            okText="Archive"
            title="Are you sure you want to archive this target?"
            tooltipTitle={getTooltipTitle()}
            updateRequestApi={updateTarget}
            updateRequestCallback={reloadData}
            updateRequestData={resourceId}
            updateRequestParams={{ state: ARCHIVED }}
            updateRequestSuccessMesg="Target successfully archived"
        />
    )
}

export default memo(ArchiveTargetField, isEqual) as typeof ArchiveTargetField
